import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CsvFormatHelpModal } from '@/components/CsvFormatHelpModal/CsvFormatHelpModal';
import {
  CSV_TEMPLATE_HEADERS,
  mapCsvRow,
  validateHeaders,
  validateRows,
  type CsvRowError,
  type HeaderValidationResult,
  type ParsedCsvRow,
  type RawCsvRow,
} from '@/lib/admin-deal-csv-schemas';
import csvTemplateContent from '@/lib/assets/deals_template_valid.csv?raw';
import { serializeRowsToCsv } from '@/lib/csv-export';
import { CSV_MAX_BYTES, CSV_MAX_ROWS, parseCsv, type CsvParseError } from '@/lib/csv-parse';
import { importDealsCsv, type AdminDealCsvImportRow } from '@/lib/db/admin/deals-csv-import';
import { mapPostgrestError } from '@/lib/postgrest-error-map';

type ImportStage = 'idle' | 'parsed' | 'submitting' | 'done';

type ParseFailure =
  | { kind: 'parse'; code: CsvParseError['code'] | 'encoding'; message: string }
  | { kind: 'headers'; missing: string[]; extra: string[] };

// Heuristic threshold for U+FFFD replacement characters that mark a file
// decoded with the wrong codec. A handful of legitimate emoji could produce
// FFFD in pathological cases but never more than a couple in a real CSV, so
// 5 keeps the false-positive rate near zero. (ADR-022 §D1.)
const ENCODING_REPLACEMENT_THRESHOLD = 5;

function downloadBlob(content: string, filename: string, mime = 'text/csv'): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AdminImportPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<ImportStage>('idle');
  const [filename, setFilename] = useState<string>('');
  const [parsed, setParsed] = useState<ParsedCsvRow[]>([]);
  const [parseFailure, setParseFailure] = useState<ParseFailure | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const steps = t('admin.import.steps', { returnObjects: true }) as string[];
  const headers = t('admin.import.headers', { returnObjects: true }) as string[];

  const validCount = useMemo(() => parsed.filter((r) => r.ok).length, [parsed]);
  const errorCount = useMemo(() => parsed.filter((r) => !r.ok).length, [parsed]);

  const reset = () => {
    setStage('idle');
    setFilename('');
    setParsed([]);
    setParseFailure(null);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const readFileAsText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
      reader.readAsText(file);
    });

  const handleDownloadTemplate = () => {
    downloadBlob(csvTemplateContent, 'deals_template.csv');
  };

  const handleDownloadErrors = () => {
    const errorRows = parsed.filter((r): r is Extract<ParsedCsvRow, { ok: false }> => !r.ok);
    if (errorRows.length === 0) return;
    const rows = errorRows.map((r) => ({
      ...r.rawRow,
      _errors: r.errors.map((e) => `${e.path}: ${e.message}`).join(' · '),
    }));
    downloadBlob(
      serializeRowsToCsv([...CSV_TEMPLATE_HEADERS, '_errors'], rows),
      'deals_errors.csv',
    );
    toast.info(t('admin.import.errorsExported', { count: errorRows.length }));
  };

  const formatHeaderBannerMessage = (headers: HeaderValidationResult): string => {
    const parts: string[] = [];
    if (headers.missing.length > 0) {
      parts.push(t('admin.import.errors.missingHeaders', { list: headers.missing.join(', ') }));
    }
    if (headers.extra.length > 0) {
      parts.push(t('admin.import.errors.extraHeaders', { list: headers.extra.join(', ') }));
    }
    return parts.join(' · ');
  };

  const handleFile = async (file: File) => {
    setSubmitError(null);
    setParseFailure(null);
    if (file.size > CSV_MAX_BYTES) {
      setParseFailure({
        kind: 'parse',
        code: 'tooLarge',
        message: t('admin.import.errors.tooLarge'),
      });
      setStage('idle');
      return;
    }
    setFilename(file.name);
    const text = await readFileAsText(file);

    // Encoding heuristic: U+FFFD replacement chars > threshold ⇒ the file was
    // probably saved in Windows-1252 / Latin-1, FileReader has degraded a few
    // bytes to FFFD. Bail out before the admin sees rows full of "�" in the
    // preview and gets confused by cascading Zod errors.
    const replacementCount = (text.match(/�/g) ?? []).length;
    if (replacementCount > ENCODING_REPLACEMENT_THRESHOLD) {
      setParseFailure({
        kind: 'parse',
        code: 'encoding',
        message: t('admin.import.errors.encoding'),
      });
      setStage('idle');
      return;
    }

    const result = parseCsv(text);
    if (!result.ok) {
      setParseFailure({
        kind: 'parse',
        code: result.code,
        message: t(`admin.import.errors.${result.code}`, { defaultValue: result.message }),
      });
      setStage('idle');
      return;
    }
    const headerCheck = validateHeaders(result.headers);
    if (headerCheck.missing.length > 0) {
      setParseFailure({
        kind: 'headers',
        missing: headerCheck.missing,
        extra: headerCheck.extra,
      });
      setStage('idle');
      return;
    }
    const pairs: { raw: RawCsvRow; mapped: ReturnType<typeof mapCsvRow> }[] = result.rows.map(
      (raw) => ({ raw, mapped: mapCsvRow(raw) }),
    );
    const validated = validateRows(pairs);
    setParsed(validated);
    if (headerCheck.extra.length > 0) {
      // Surface the extras as a soft warning — non-blocking, just FYI.
      toast.info(t('admin.import.errors.extraHeaders', { list: headerCheck.extra.join(', ') }));
    }
    setStage('parsed');
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const onFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void handleFile(file);
  };

  const humanizeError = (e: CsvRowError): string => {
    const i18nKey = `admin.import.zodErrors.${e.code}`;
    return t(i18nKey, { defaultValue: `${e.path}: ${e.message}` });
  };

  const handleImport = async () => {
    if (stage !== 'parsed') return;
    const rows: AdminDealCsvImportRow[] = parsed
      .filter((r): r is Extract<ParsedCsvRow, { ok: true }> => r.ok)
      .map((r) => r.data);
    if (rows.length === 0) return;

    setStage('submitting');
    setSubmitError(null);
    try {
      const result = await importDealsCsv(rows);
      toast.success(
        t('admin.import.successToast', {
          inserted: result.inserted_count,
          total: parsed.length,
          errors: errorCount,
        }),
      );
      setStage('done');
    } catch (err) {
      const mapped = mapPostgrestError(err);
      const msg = t(mapped.i18nKey, { defaultValue: t('admin.import.errors.generic') });
      setSubmitError(msg);
      toast.error(msg);
      setStage('parsed');
    }
  };

  const stepIndex = stage === 'idle' ? 1 : stage === 'done' ? 3 : 2;
  const dropzoneClass = `admin-dropzone${dragOver ? ' is-dragover' : ''}`;

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.import.title')}</h1>
        </div>
        <div className="admin-head__actions">
          <button
            type="button"
            className="admin-btn admin-btn--outline"
            onClick={() => setHelpOpen(true)}
          >
            {t('admin.import.help.button')}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--outline"
            onClick={handleDownloadTemplate}
          >
            {t('admin.import.downloadTemplate')}
          </button>
        </div>
      </header>

      <div className="admin-stepper">
        {steps.map((label, i) => (
          <div key={label} className={`admin-stepper__step${i === stepIndex ? ' is-active' : ''}`}>
            <span className="admin-stepper__num">0{i + 1}</span>
            <span className="admin-stepper__label">{label}</span>
          </div>
        ))}
      </div>

      {parseFailure ? (
        <div
          className="admin-warn-banner admin-warn-banner--error"
          role="alert"
          style={{ marginTop: 16 }}
        >
          <div className="admin-warn-banner__body">
            {parseFailure.kind === 'headers'
              ? formatHeaderBannerMessage({
                  missing: parseFailure.missing,
                  extra: parseFailure.extra,
                  normalizedMap: {},
                })
              : parseFailure.message}
          </div>
        </div>
      ) : null}

      {stage === 'idle' || stage === 'done' ? (
        <div
          role="button"
          tabIndex={0}
          aria-label={t('admin.import.drop')}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={dropzoneClass}
        >
          <div className="admin-dropzone__title">{t('admin.import.drop')}</div>
          <div className="admin-dropzone__sub">
            {t('admin.import.dropHint', { maxRows: CSV_MAX_ROWS, maxBytesMb: 10 })}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileInput}
            style={{ display: 'none' }}
            data-testid="admin-import-file-input"
          />
        </div>
      ) : (
        <>
          <div className="admin-uploaded">
            <span className="admin-uploaded__file">
              {t('admin.import.fileSummary', {
                filename,
                rows: parsed.length,
                errors: errorCount,
              })}
            </span>
            <button
              type="button"
              className="admin-bulk-clear"
              style={{ marginInlineStart: 0 }}
              onClick={reset}
            >
              {t('admin.import.changeFile')}
            </button>
          </div>

          {submitError ? (
            <div
              className="admin-warn-banner admin-warn-banner--error"
              role="alert"
              style={{ marginTop: 16 }}
            >
              <div className="admin-warn-banner__body">{submitError}</div>
            </div>
          ) : null}

          <div className="admin-table-wrap">
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--ddm-paper)',
                borderBottom: '1px solid var(--ddm-neutral-100)',
                fontFamily: 'var(--ddm-font-sans)',
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                color: 'var(--ddm-neutral-400)',
                textTransform: 'uppercase',
              }}
            >
              {t('admin.import.previewLabel', {
                rows: parsed.length,
                errors: errorCount,
                valid: validCount,
              })}
            </div>
            <table className="admin-table" style={{ minWidth: 500 }}>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.map((row) => {
                  const lineNumber = String(row.index + 2).padStart(2, '0');
                  if (row.ok) {
                    return (
                      <tr key={row.index}>
                        <td className="admin-cell--mono">{lineNumber}</td>
                        <td className="admin-cell--mono">{row.data.type}</td>
                        <td className="admin-cell--mono">{row.data.zone}</td>
                        <td className="admin-cell--mono">{row.data.community}</td>
                        <td className="admin-cell--mono">{row.data.bedrooms}</td>
                        <td className="admin-cell--mono">{row.data.asking_price}</td>
                        <td>
                          <span className="admin-cell--positive">✓</span>
                        </td>
                      </tr>
                    );
                  }
                  // Render the admin's raw values so they can correlate the
                  // error with what's in their spreadsheet without leaving the
                  // page (ADR-022 §D5).
                  return (
                    <tr key={row.index} style={{ background: 'rgba(153,60,29,0.04)' }}>
                      <td className="admin-cell--mono">{lineNumber}</td>
                      <td className="admin-cell--mono">{row.rawRow.type ?? '—'}</td>
                      <td className="admin-cell--mono">{row.rawRow.zone ?? '—'}</td>
                      <td className="admin-cell--mono">{row.rawRow.community ?? '—'}</td>
                      <td className="admin-cell--mono">{row.rawRow.bedrooms ?? '—'}</td>
                      <td className="admin-cell--mono">{row.rawRow.price_aed ?? '—'}</td>
                      <td>
                        <span
                          className="admin-cell--mono"
                          style={{ color: 'var(--ddm-alert)' }}
                          title={row.errors.map(humanizeError).join(' · ')}
                        >
                          ✗ {row.errors.map(humanizeError).join(' · ')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {stage === 'submitting' ? (
            <div className="admin-warn-banner" style={{ marginTop: 16, marginBottom: 0 }}>
              <div className="admin-warn-banner__body" style={{ flex: 1 }}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('admin.import.atomicity', {
                      rows: validCount,
                    }),
                  }}
                />
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="admin-btn"
              disabled={stage === 'submitting' || validCount === 0}
              onClick={handleImport}
            >
              {stage === 'submitting'
                ? t('admin.import.submitting')
                : t('admin.import.submit', { count: validCount })}
            </button>
            {errorCount > 0 ? (
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={handleDownloadErrors}
                disabled={stage === 'submitting'}
              >
                {t('admin.import.downloadErrors', { count: errorCount })}
              </button>
            ) : null}
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              onClick={reset}
              disabled={stage === 'submitting'}
            >
              {t('admin.import.fixAndReupload')}
            </button>
          </div>
        </>
      )}

      <CsvFormatHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onDownloadTemplate={handleDownloadTemplate}
      />
    </>
  );
}
